import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Play,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Users,
  Boxes,
  Loader2,
  BarChart3,
  Zap,
  TrendingUp,
  Eye,
  Send,
  RefreshCw,
  ChevronRight,
  PlaneTakeoff,
  PlaneLanding,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiGet, apiPost } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { API_ROUTES, Role } from '@avgrp/shared';
import { hasMinRole } from '@/utils/roles';

// ─── Types (matching backend responses) ────────────────────────────

interface FlightTask {
  id: string;
  name: string;
  phase: string;
  status: string;
  priority: number;
  plannedStart: string;
  plannedEnd: string;
  assignments: {
    id: string;
    resourceType: string;
    status: string;
    user?: { id: string; firstName: string; lastName: string; employeeId: string };
    material?: { id: string; serialNumber: string; materialType: unknown };
  }[];
  taskTemplate?: { id: string; name: string; code: string };
}

interface ScheduleFlight {
  id: string;
  flightNumber: string;
  direction: 'DEPARTURE' | 'ARRIVAL';
  std: string;
  sta: string;
  status: string;
  gate?: string;
  aircraft: {
    registration: string;
    aircraftType: { name: string; icaoCode: string };
  };
  origin: { iataCode: string };
  destination: { iataCode: string };
  flightTasks: FlightTask[];
}

interface ScheduleSummary {
  totalFlights: number;
  totalTasks: number;
  assignedTasks: number;
  pendingTasks: number;
  totalAssignments: number;
}

interface SchedulerStats {
  totalFlights: number;
  totalTasks: number;
  assignedTasks: number;
  unassignedTasks: number;
  employeesUsed: number;
  materialsUsed: number;
  avgEmployeeUtilization: number;
  totalCost: number;
  executionTimeMs: number;
}

interface GeneratePreview {
  preview: boolean;
  assignments: {
    taskTemplateId: string;
    flightId: string;
    resourceType: 'HUMAN' | 'MATERIAL';
    userId?: string;
    materialId?: string;
    plannedStart: string;
    plannedEnd: string;
    cost: number;
  }[];
  unassigned: {
    taskTemplateId: string;
    flightId: string;
    flightNumber: string;
    taskName: string;
    reason: string;
  }[];
  conflicts: {
    type: string;
    message: string;
    flightId: string;
    resourceId?: string;
  }[];
  stats: SchedulerStats;
  totalCost: number;
}

interface ApplyResult {
  applied: boolean;
  stats: SchedulerStats;
  unassigned: GeneratePreview['unassigned'];
  conflicts: GeneratePreview['conflicts'];
  notifiedEmployees: number;
}

// ─── Component ──────────────────────────────────────────────

export function SchedulingPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [activeTab, setActiveTab] = useState<'overview' | 'assignments' | 'unassigned' | 'conflicts'>('overview');
  const [preview, setPreview] = useState<GeneratePreview | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [selectedFlightIds, setSelectedFlightIds] = useState<Set<string>>(new Set());

  // Fetch current schedule (persisted data)
  const { data: scheduleData, isLoading } = useQuery({
    queryKey: ['scheduling', selectedDate],
    queryFn: () =>
      apiGet<{ data: ScheduleFlight[]; summary: ScheduleSummary }>(API_ROUTES.SCHEDULING, {
        params: { baseId: user?.baseId, date: selectedDate },
      }),
    retry: false,
  });

  const flights = scheduleData?.data ?? [];
  const summary = scheduleData?.summary ?? { totalFlights: 0, totalTasks: 0, assignedTasks: 0, pendingTasks: 0, totalAssignments: 0 };

  // Generate schedule preview
  const generateMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: GeneratePreview }>(`${API_ROUTES.SCHEDULING}/generate`, {
        baseId: user?.baseId,
        date: selectedDate,
        flightIds: selectedFlightIds.size > 0 ? Array.from(selectedFlightIds) : undefined,
      }),
    onSuccess: (res) => {
      setPreview(res.data);
      setApplyResult(null);
      setActiveTab('assignments');
    },
  });

  // Apply schedule
  const applyMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: ApplyResult }>(`${API_ROUTES.SCHEDULING}/apply`, {
        baseId: user?.baseId,
        date: selectedDate,
        flightIds: selectedFlightIds.size > 0 ? Array.from(selectedFlightIds) : undefined,
      }),
    onSuccess: (res) => {
      setApplyResult(res.data);
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ['scheduling', selectedDate] });
    },
  });

  // Clear overdue tasks
  const clearOverdueMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: { cleared: number } }>(`${API_ROUTES.TASKS}/clear-overdue`, {
        baseId: user?.baseId,
        date: selectedDate,
      }),
    onSuccess: (res) => {
      setClearedCount(res.data.cleared);
      queryClient.invalidateQueries({ queryKey: ['scheduling', selectedDate] });
      setTimeout(() => setClearedCount(null), 8000);
    },
  });
  const [clearedCount, setClearedCount] = useState<number | null>(null);

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setPreview(null);
    setApplyResult(null);
    setActiveTab('overview');
    setClearedCount(null);
  };

  // Stats to display
  const stats = preview?.stats;

  // Map flight IDs to flight numbers for display
  const flightNumberMap = new Map<string, string>();
  for (const f of flights) {
    flightNumberMap.set(f.id, f.flightNumber);
  }

  const taskStatusColors: Record<string, string> = {
    PENDING: 'bg-gray-100 text-gray-700',
    ASSIGNED: 'bg-amber-100 text-amber-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    CANCELLED: 'bg-red-100 text-red-700',
    BLOCKED: 'bg-red-100 text-red-700',
    SKIPPED: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auto Scheduling</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generate optimized resource assignments for flights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => clearOverdueMutation.mutate()}
              disabled={clearOverdueMutation.isPending}
              className="inline-flex items-center rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
            >
              {clearOverdueMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="mr-2 h-4 w-4" />
              )}
              {clearOverdueMutation.isPending ? 'Clearing...' : 'Clear Overdue'}
            </button>
          )}
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {generateMutation.isPending
                ? 'Generating...'
                : selectedFlightIds.size > 0
                  ? `Schedule ${selectedFlightIds.size} Flight${selectedFlightIds.size > 1 ? 's' : ''}`
                  : 'Generate Schedule'}
            </button>
          )}
        </div>
      </div>

      {/* Clear overdue result */}
      {clearedCount !== null && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-amber-600" />
          {clearedCount === 0
            ? 'No overdue tasks found'
            : `${clearedCount} overdue task${clearedCount > 1 ? 's' : ''} cleared`}
        </div>
      )}

      {/* Error display */}
      {generateMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <p className="font-medium">Failed to generate schedule</p>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {(generateMutation.error as Error)?.message || 'An unexpected error occurred'}
          </p>
        </div>
      )}

      {applyMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <p className="font-medium">Failed to apply schedule</p>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {(applyMutation.error as Error)?.message || 'An unexpected error occurred'}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        </div>
      ) : (
        <>
          {/* Current State Summary Cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-2">
                  <PlaneTakeoff className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Flights</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalFlights}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-50 p-2">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Tasks</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalTasks}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-emerald-50 p-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Assigned</p>
                  <p className="text-xl font-bold text-gray-900">{summary.assignedTasks}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-xl font-bold text-gray-900">{summary.pendingTasks}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-indigo-50 p-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Assignments</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalAssignments}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Generate Preview Results */}
          {preview && (
            <div className="space-y-4">
              {/* Preview Stats */}
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-blue-900">Schedule Preview</h3>
                      <p className="text-sm text-blue-600">
                        {stats!.assignedTasks} assignments across {stats!.totalFlights} flights •
                        {stats!.employeesUsed} employees •
                        {stats!.avgEmployeeUtilization.toFixed(0)}% avg utilization •
                        Generated in {(stats!.executionTimeMs / 1000).toFixed(1)}s
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setPreview(null); setActiveTab('overview'); }}
                      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Dismiss
                    </button>
                    {hasMinRole(user?.role, Role.MANAGER) && (
                      <button
                        onClick={() => applyMutation.mutate()}
                        disabled={applyMutation.isPending}
                        className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {applyMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        Apply Schedule
                      </button>
                    )}
                  </div>
                </div>

                {/* Preview Stats Cards */}
                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">Tasks Assigned</p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats!.assignedTasks}
                      <span className="text-sm font-normal text-gray-400">/{stats!.totalTasks}</span>
                    </p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">Employees Used</p>
                    <p className="text-lg font-bold text-gray-900">{stats!.employeesUsed}</p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">Unassigned</p>
                    <p className="text-lg font-bold text-amber-600">{stats!.unassignedTasks}</p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">Est. Cost</p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats!.totalCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} DZD
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs for preview details */}
              <div className="rounded-xl border border-gray-200 bg-white">
                <div className="flex gap-1 border-b border-gray-200 p-3">
                  <button
                    onClick={() => setActiveTab('assignments')}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors
                      ${activeTab === 'assignments' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Assignments ({preview.assignments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('unassigned')}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors
                      ${activeTab === 'unassigned' ? 'bg-amber-50 text-amber-700' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    <XCircle className="h-4 w-4" />
                    Unassigned ({preview.unassigned.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('conflicts')}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors
                      ${activeTab === 'conflicts' ? 'bg-red-50 text-red-700' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Conflicts ({preview.conflicts.length})
                  </button>
                </div>

                <div className="p-4">
                  {/* Assignments tab */}
                  {activeTab === 'assignments' && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="pb-2 text-left font-medium text-gray-500">Type</th>
                            <th className="pb-2 text-left font-medium text-gray-500">Flight</th>
                            <th className="pb-2 text-left font-medium text-gray-500">Start</th>
                            <th className="pb-2 text-left font-medium text-gray-500">End</th>
                            <th className="pb-2 text-left font-medium text-gray-500">Duration</th>
                            <th className="pb-2 text-left font-medium text-gray-500">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.assignments.map((a, idx) => (
                            <tr key={idx} className="border-b border-gray-50 last:border-0">
                              <td className="py-2">
                                <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                                  a.resourceType === 'HUMAN' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                                }`}>
                                  {a.resourceType === 'HUMAN' ? <Users className="h-3 w-3" /> : <Boxes className="h-3 w-3" />}
                                  {a.resourceType}
                                </span>
                              </td>
                              <td className="py-2 font-mono text-xs">{flightNumberMap.get(a.flightId) || a.flightId.slice(0, 8)}</td>
                              <td className="py-2 font-mono text-xs">{dayjs(a.plannedStart).format('HH:mm')}</td>
                              <td className="py-2 font-mono text-xs">{dayjs(a.plannedEnd).format('HH:mm')}</td>
                              <td className="py-2 text-gray-500">
                                {dayjs(a.plannedEnd).diff(dayjs(a.plannedStart), 'minute')}m
                              </td>
                              <td className="py-2 font-mono text-xs">{a.cost.toFixed(0)} DZD</td>
                            </tr>
                          ))}
                          {preview.assignments.length === 0 && (
                            <tr>
                              <td colSpan={6} className="py-12 text-center text-gray-400">
                                No assignments in preview
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Unassigned tab */}
                  {activeTab === 'unassigned' && (
                    <div className="space-y-3">
                      {preview.unassigned.map((task, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
                        >
                          <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {task.taskName}
                              <span className="ml-2 font-mono text-sm text-gray-500">{task.flightNumber}</span>
                            </p>
                            <p className="mt-1 text-sm text-amber-700">{task.reason}</p>
                          </div>
                        </div>
                      ))}
                      {preview.unassigned.length === 0 && (
                        <div className="py-12 text-center">
                          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
                          <p className="mt-3 text-sm text-gray-400">All tasks assigned successfully</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Conflicts tab */}
                  {activeTab === 'conflicts' && (
                    <div className="space-y-3">
                      {preview.conflicts.map((conflict, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
                        >
                          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                          <div>
                            <p className="font-medium text-red-800">{conflict.type}</p>
                            <p className="mt-1 text-sm text-red-600">{conflict.message}</p>
                          </div>
                        </div>
                      ))}
                      {preview.conflicts.length === 0 && (
                        <div className="py-12 text-center">
                          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
                          <p className="mt-3 text-sm text-gray-400">No conflicts detected</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Apply Success Banner */}
          {applyResult && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div>
                  <h3 className="font-semibold text-emerald-900">Schedule Applied Successfully</h3>
                  <p className="text-sm text-emerald-700">
                    {applyResult.stats.assignedTasks} assignments created •
                    {applyResult.notifiedEmployees} employees notified •
                    {applyResult.stats.unassignedTasks} unassigned tasks
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Flights / Tasks Overview (from GET /) */}
          {!preview && (
            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">
                    Flights & Tasks — {dayjs(selectedDate).format('DD MMM YYYY')}
                  </h3>
                  {selectedFlightIds.size > 0 && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {selectedFlightIds.size} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {flights.length > 0 && (
                    <>
                      <button
                        onClick={() => {
                          if (selectedFlightIds.size === flights.length) {
                            setSelectedFlightIds(new Set());
                          } else {
                            setSelectedFlightIds(new Set(flights.map(f => f.id)));
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                      >
                        {selectedFlightIds.size === flights.length ? 'Deselect All' : 'Select All'}
                      </button>
                      {selectedFlightIds.size > 0 && (
                        <button
                          onClick={() => setSelectedFlightIds(new Set())}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['scheduling', selectedDate] })}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh
                  </button>
                </div>
              </div>

              {flights.length === 0 ? (
                <div className="py-16 text-center">
                  <CalendarClock className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-4 text-lg font-semibold text-gray-700">No flights for this date</h3>
                  <p className="mt-2 text-sm text-gray-400">
                    There are no flights scheduled for {dayjs(selectedDate).format('DD MMM YYYY')}.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {flights.map((flight) => {
                    const assignedCount = flight.flightTasks.filter(t => t.assignments.length > 0).length;
                    const totalCount = flight.flightTasks.length;
                    const isDep = flight.direction === 'DEPARTURE';

                    return (
                      <div key={flight.id} className={`p-4 ${selectedFlightIds.has(flight.id) ? 'bg-blue-50/30' : ''}`}>
                        {/* Flight header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedFlightIds.has(flight.id)}
                              onChange={() => {
                                const next = new Set(selectedFlightIds);
                                if (next.has(flight.id)) {
                                  next.delete(flight.id);
                                } else {
                                  next.add(flight.id);
                                }
                                setSelectedFlightIds(next);
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className={`rounded-lg p-2 ${isDep ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                              {isDep ? (
                                <PlaneTakeoff className={`h-4 w-4 ${isDep ? 'text-blue-600' : 'text-emerald-600'}`} />
                              ) : (
                                <PlaneLanding className="h-4 w-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-semibold text-gray-900">{flight.flightNumber}</span>
                                <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${isDep ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {flight.direction}
                                </span>
                                <span className="text-xs text-gray-400">{flight.aircraft.registration}</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <span>{flight.origin.iataCode}</span>
                                <ChevronRight className="h-3 w-3" />
                                <span>{flight.destination.iataCode}</span>
                                <span className="ml-2">
                                  STD {dayjs(flight.std).format('HH:mm')} • STA {dayjs(flight.sta).format('HH:mm')}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-700">
                              {assignedCount}/{totalCount} tasks assigned
                            </div>
                            {totalCount > 0 && (
                              <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
                                <div
                                  className="h-full rounded-full bg-emerald-500 transition-all"
                                  style={{ width: `${totalCount > 0 ? (assignedCount / totalCount) * 100 : 0}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Tasks list */}
                        {flight.flightTasks.length > 0 && (
                          <div className="mt-3 ml-11 space-y-1.5">
                            {flight.flightTasks.map((task) => (
                              <div key={task.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${taskStatusColors[task.status] || 'bg-gray-100 text-gray-600'}`}>
                                    {task.status}
                                  </span>
                                  <span className="font-medium text-gray-700">{task.name}</span>
                                  <span className="text-xs text-gray-400">({task.phase})</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <span className="font-mono">
                                    {dayjs(task.plannedStart).format('HH:mm')}–{dayjs(task.plannedEnd).format('HH:mm')}
                                  </span>
                                  {task.assignments.length > 0 ? (
                                    <span className="flex items-center gap-1 text-emerald-600">
                                      <Users className="h-3 w-3" />
                                      {task.assignments.map(a =>
                                        a.user ? `${a.user.firstName} ${a.user.lastName.charAt(0)}.` : a.material?.serialNumber || 'Resource'
                                      ).join(', ')}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">Unassigned</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
